/* istanbul ignore file */
import React, { useCallback, useRef, useState } from 'react'
import _ from 'lodash'
import classnames from 'classnames/bind'
import { Input, Dropdown } from 'semantic-ui-react'
import type { Node } from '../../../types'
import { AutomationTarget } from '../../../types'
import Text from '../text'
import Button from '../button'
import Icon from '../icon'
import {
  type JsonViewerClassNames,
  isArrayOfObjects,
  toProperCase,
  ReadOnlyValue,
  ReadOnlyPrimitiveArray,
  ReadOnlyEmptyObject,
  NestedKeyLabel,
  getBackgroundClass,
} from '../shared/jsonRendering'
import {
  type JsonEditorSchema,
  type JsonFieldSchema,
  type SchemaToType,
  resolveFieldSchema,
  schemaMatchesValue,
  generateDefault,
  updateAtPath,
  removeAtIndex,
  appendToArray,
  removeKey,
} from './schema'
import viewerStyles from '../jsonViewer/styles.scss'
import editorStyles from './styles.scss'

const cxViewer = classnames.bind(viewerStyles)
const cxEditor = classnames.bind(editorStyles)

// Re-export schema types for consumer convenience
export type { JsonEditorSchema, JsonFieldSchema, SchemaToType }

// ---------------------------------------------------------------------------
// Boolean dropdown options (reused)
// ---------------------------------------------------------------------------

const BOOLEAN_OPTIONS = [
  { key: 'true', text: 'true', value: 'true' },
  { key: 'false', text: 'false', value: 'false' },
]

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface JsonEditorProps<T extends Node = Node> extends AutomationTarget {
  /** The JSON data to display and optionally edit */
  data: T
  /** Schema describing the structure and edit rules. Required for editing. */
  schema: JsonEditorSchema
  /**
   * Called with the full updated data tree whenever an edit is made.
   * If not provided, the component renders as read-only even with a schema.
   */
  onChange?: (updatedData: T) => void
  classNames?: JsonViewerClassNames
}

// ---------------------------------------------------------------------------
// Inline edit controls (using Semantic UI components)
// ---------------------------------------------------------------------------

const InlineBooleanField: React.FC<{
  value: boolean
  onChange: (value: boolean) => void
  elmId: string
}> = ({ value, onChange, elmId }) => (
  <div className={cxEditor('inline-field')}>
    <Dropdown
      selection
      compact
      value={String(value)}
      options={BOOLEAN_OPTIONS}
      onChange={(_, { value: v }) => onChange(v === 'true')}
      data-elm-id={elmId}
    />
  </div>
)

// Fires onChange on every keystroke. Before `dataRef`, this would recreate
// the entire handler chain (handleFieldChange → every child's props) on each
// keypress, triggering a full tree re-render. With `dataRef` the handlers are
// referentially stable so the cost is just the shallow-spread walk in
// `updateAtPath` — cheap enough that debouncing isn't necessary.
const InlineStringField: React.FC<{
  value: string
  onChange: (value: string) => void
  elmId: string
}> = ({ value, onChange, elmId }) => (
  <div className={cxEditor('inline-field')}>
    <Input
      size='mini'
      fluid
      value={value}
      onChange={(_, { value: v }) => onChange(v)}
      data-elm-id={elmId}
    />
  </div>
)

const InlineNumberField: React.FC<{
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  elmId: string
}> = ({ value, onChange, min, max, elmId }) => {
  // Local state allows free typing; clamping happens on blur so that
  // intermediate values (e.g. typing "1" on the way to "150" with min=100)
  // don't fight the user.
  const [localValue, setLocalValue] = React.useState<string>(String(value))

  // Sync from parent when the prop changes externally
  React.useEffect(() => {
    setLocalValue(String(value))
  }, [value])

  const handleBlur = React.useCallback(() => {
    const num = parseFloat(localValue)
    if (isNaN(num)) {
      // Revert to the last valid value
      setLocalValue(String(value))
      return
    }
    const clamped =
      min != null && num < min ? min : max != null && num > max ? max : num
    setLocalValue(String(clamped))
    onChange(clamped)
  }, [localValue, value, min, max, onChange])

  return (
    <div className={cxEditor('inline-field', 'inline-number')}>
      <Input
        size='mini'
        fluid
        type='number'
        value={localValue}
        onChange={(_, { value: v }) => setLocalValue(v)}
        onBlur={handleBlur}
        min={min}
        max={max}
        data-elm-id={elmId}
      />
    </div>
  )
}

const InlineEnumField: React.FC<{
  value: string
  options: string[]
  onChange: (value: string) => void
  elmId: string
}> = ({ value, options, onChange, elmId }) => (
  <div className={cxEditor('inline-field')} title={value}>
    <Dropdown
      selection
      compact
      value={value}
      options={options.map(opt => ({
        key: opt,
        text: opt,
        value: opt,
        title: opt,
      }))}
      onChange={(_, { value: v }) => onChange(v as string)}
      data-elm-id={elmId}
    />
  </div>
)

/**
 * Renders the correct inline edit control for a given schema field type.
 * Centralises the boolean/string/number/enum switch that would otherwise be
 * duplicated in `renderEditableValue` and `EditorArrayNode`.
 */
const InlineFieldByType: React.FC<{
  value: Node
  schema: JsonFieldSchema
  onChange: (value: Node) => void
  elmId: string
}> = ({ value, schema, onChange, elmId }) => (
  <>
    {schema.type === 'boolean' && (
      <InlineBooleanField
        value={value as boolean}
        onChange={onChange}
        elmId={elmId}
      />
    )}
    {schema.type === 'string' && (
      <InlineStringField
        value={value as string}
        onChange={onChange}
        elmId={elmId}
      />
    )}
    {schema.type === 'number' && (
      <InlineNumberField
        value={value as number}
        onChange={onChange}
        min={schema.min}
        max={schema.max}
        elmId={elmId}
      />
    )}
    {schema.type === 'enum' && (
      <InlineEnumField
        value={value as string}
        options={schema.options}
        onChange={onChange}
        elmId={elmId}
      />
    )}
  </>
)

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * JsonEditor — a schema-driven editable JSON viewer.
 *
 * Renders the same visual structure as `JsonViewer` but replaces primitive
 * values with inline edit controls when a matching, editable schema field
 * exists and an `onChange` callback is provided.
 *
 * Each block (container) must be explicitly toggled into edit mode via a
 * pencil icon. Fields without a matching schema, or where the schema type
 * doesn't match the runtime data, gracefully fall back to read-only display.
 *
 * @typeParam T — the TypeScript type of `data`. When a typed schema is used,
 *   pass `SchemaToType<typeof mySchema.root>` to get compile-time verification
 *   that data and schema agree.
 */
const JsonEditor = <T extends Node = Node>({
  data,
  schema,
  onChange,
  elmId = 'json_editor',
  classNames,
}: JsonEditorProps<T>): React.ReactElement => {
  // Track which blocks are currently in edit mode (by serialised path)
  const [editingPaths, setEditingPaths] = useState<Set<string>>(new Set())

  // Stable ref to the latest data so handlers don't need `data` in their
  // dependency arrays — avoids recreating every callback on each keystroke
  // which would cascade new props through every React.memo'd child.
  const dataRef = useRef(data)
  dataRef.current = data

  const toggleEditing = useCallback((pathKey: string) => {
    setEditingPaths(prev => {
      const next = new Set(prev)
      if (next.has(pathKey)) {
        next.delete(pathKey)
      } else {
        next.add(pathKey)
      }
      return next
    })
  }, [])

  /** Walk `dataRef.current` down the given path segments to find the target node. */
  const resolveTarget = (segments: (string | number)[]): Node => {
    let target: Node = dataRef.current
    for (const segment of segments) {
      if (Array.isArray(target)) {
        target = target[segment as number]
      } else if (typeof target === 'object' && target !== null) {
        target = (target as Record<string, Node>)[segment as string]
      }
    }
    return target
  }

  const handleFieldChange = useCallback(
    (path: (string | number)[], newValue: Node) => {
      if (!onChange) return
      onChange(updateAtPath(dataRef.current, path, newValue) as T)
    },
    [onChange]
  )

  const handleDeleteItem = useCallback(
    (path: (string | number)[], index: number) => {
      if (!onChange) return
      const target = resolveTarget(path)
      const newArray = removeAtIndex(target, index)
      const updated =
        path.length === 0
          ? (newArray as T)
          : (updateAtPath(dataRef.current, path, newArray) as T)
      onChange(updated)
    },
    [onChange]
  )

  const handleAddItem = useCallback(
    (path: (string | number)[], itemSchema: JsonFieldSchema) => {
      if (!onChange) return
      const defaultValue = generateDefault(itemSchema)
      const target = resolveTarget(path)
      const newArray = appendToArray(target, defaultValue)
      const updated =
        path.length === 0
          ? (newArray as T)
          : (updateAtPath(dataRef.current, path, newArray) as T)
      onChange(updated)
    },
    [onChange]
  )

  const handleDeleteKey = useCallback(
    (objectPath: (string | number)[], key: string) => {
      if (!onChange) return
      const target = resolveTarget(objectPath)
      const newObj = removeKey(target, key)
      const updated =
        objectPath.length === 0
          ? (newObj as T)
          : (updateAtPath(dataRef.current, objectPath, newObj) as T)
      onChange(updated)
    },
    [onChange]
  )

  return (
    <div
      className={cxViewer(
        'json-viewer',
        { 'is-editing': editingPaths.size > 0 },
        classNames?.viewer
      )}
      data-elm-id={elmId}
    >
      <EditorNode
        data={data}
        schema={schema?.root}
        overrides={schema?.overrides}
        level={0}
        path={[]}
        classNames={classNames}
        onFieldChange={handleFieldChange}
        onDeleteItem={handleDeleteItem}
        onAddItem={handleAddItem}
        onDeleteKey={handleDeleteKey}
        canEdit={!!onChange}
        editingPaths={editingPaths}
        toggleEditing={toggleEditing}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Serialise a path array into a stable string key for the editing set. */
const pathKey = (path: (string | number)[]): string => path.join('/')

/**
 * Returns true when a schema contains at least one editable primitive field.
 * For object schemas, checks direct properties only (does not recurse into
 * nested objects). For array schemas, recurses into `items` to determine if
 * array elements are editable.
 * Returns false if the schema itself is marked `editable: false`.
 */
const hasEditableFields = (schema: JsonFieldSchema | undefined): boolean => {
  if (!schema) return false
  if (schema.editable === false) return false
  if (schema.type === 'object' && schema.properties) {
    return Object.values(schema.properties).some(
      (f: JsonFieldSchema) =>
        f.editable &&
        ['string', 'number', 'boolean', 'enum', 'array'].includes(f.type)
    )
  }
  if (schema.type === 'array' && schema.items) {
    return hasEditableFields(schema.items)
  }
  return !!schema.editable
}

/**
 * Returns true when any override key targets a direct primitive child of
 * `path`. Arrays and objects are excluded because they manage their own
 * edit toggle via `EditorArrayNode` / nested `EditorNode`.
 */
const hasOverrideChildren = (
  overrides: Record<string, JsonFieldSchema> | undefined,
  path: (string | number)[]
): boolean => {
  if (!overrides) return false
  const prefix = path.length > 0 ? path.join('.') + '.' : ''
  const primitiveTypes = ['string', 'number', 'boolean', 'enum']
  return Object.entries(overrides).some(([key, fieldSchema]) => {
    if (!key.startsWith(prefix) || !fieldSchema.editable) return false
    // Only match if the remainder is a single segment (direct child, no dots)
    const remainder = key.slice(prefix.length)
    return (
      remainder.length > 0 &&
      !remainder.includes('.') &&
      primitiveTypes.includes(fieldSchema.type)
    )
  })
}

// ---------------------------------------------------------------------------
// Recursive editor node
// ---------------------------------------------------------------------------

interface EditorNodeProps {
  data: Node
  schema: JsonFieldSchema | undefined
  name?: string
  level: number
  path: (string | number)[]
  classNames?: JsonViewerClassNames
  onFieldChange: (path: (string | number)[], value: Node) => void
  onDeleteItem: (arrayPath: (string | number)[], index: number) => void
  onAddItem: (
    arrayPath: (string | number)[],
    itemSchema: JsonFieldSchema
  ) => void
  onDeleteKey: (objectPath: (string | number)[], key: string) => void
  canEdit: boolean
  editingPaths: Set<string>
  toggleEditing: (pathKey: string) => void
  /** When provided, a delete (trash) button is shown in the actions bar. */
  onDelete?: () => void
  /** Path-level schema overrides from `JsonEditorSchema.overrides`. */
  overrides?: Record<string, JsonFieldSchema>
}

const EditorNode: React.FC<EditorNodeProps> = React.memo(
  ({
    data,
    schema,
    name,
    level,
    path,
    classNames,
    onFieldChange,
    onDeleteItem,
    onAddItem,
    onDeleteKey,
    canEdit,
    editingPaths,
    toggleEditing,
    onDelete,
    overrides,
  }) => {
    const backgroundColorClass = getBackgroundClass(level)
    const isObject = typeof data === 'object' && data !== null
    const isArray = Array.isArray(data)
    const myPathKey = pathKey(path)
    const isEditing = editingPaths.has(myPathKey)
    // If this node's schema explicitly disables editing, cascade to children
    const effectiveCanEdit = canEdit && schema?.editable !== false

    // Stable ref so useCallbacks don't need editingPaths in their deps.
    // Same pattern as dataRef — avoids recreating the entire callback chain
    // every time a pencil icon is toggled anywhere in the tree.
    const editingRef = useRef(editingPaths)
    editingRef.current = editingPaths
    // Show edit toggle when there are editable fields, OR when this item is deletable
    // (so user can enter edit mode to access the delete button)
    const showEditToggle =
      effectiveCanEdit &&
      (hasEditableFields(schema) ||
        hasOverrideChildren(overrides, path) ||
        !!onDelete)
    const showActionsBar = !!name || showEditToggle

    // ── Editable primitive ──────────────────────────────────────────────
    const renderEditableValue = useCallback(
      (
        value: Node,
        keyName: string,
        fieldPath: (string | number)[],
        fieldSchema: JsonFieldSchema | undefined,
        deleteBtn?: React.ReactNode
      ) => {
        // Check if the containing block is in editing mode
        const containerKey = pathKey(fieldPath.slice(0, -1))
        const blockIsEditing = editingRef.current.has(containerKey)

        const fieldEditable =
          effectiveCanEdit &&
          blockIsEditing &&
          !!fieldSchema?.editable &&
          fieldSchema
            ? schemaMatchesValue(fieldSchema, value)
            : false

        if (!fieldEditable || !fieldSchema) {
          return (
            <div key={keyName} className={cxEditor('array-item-row')}>
              <ReadOnlyValue
                keyName={keyName}
                value={value}
                classNames={classNames}
              />
              {blockIsEditing && deleteBtn}
            </div>
          )
        }

        const elmId = fieldPath.join('_')

        return (
          <div
            className={cxViewer('json-value', classNames?.value)}
            key={keyName}
          >
            <Text size='tiny' className={cxViewer('json-key', classNames?.key)}>
              {toProperCase(keyName)}:
            </Text>
            <div className={cxEditor('editable-value')}>
              <InlineFieldByType
                value={value}
                schema={fieldSchema}
                onChange={v => onFieldChange(fieldPath, v)}
                elmId={elmId}
              />
              {deleteBtn}
            </div>
          </div>
        )
      },
      [effectiveCanEdit, classNames, onFieldChange]
    )

    // ── Render a value (either editable or read-only) ───────────────────
    const renderValue = useCallback(
      (value: Node, keyName: string, lvl: number, showDeleteBtn?: boolean) => {
        const childPath = [...path, keyName]
        // Override keys are dot-joined data paths from root (no "root" prefix).
        // Example: for data `{ query: { filters: [{ values: [...] }] } }`,
        // the override key for the first filter's values would be
        // "query.filters.0.values" and you could target it with:
        //   overrides: {
        //     "query.filters.0.values": {
        //       type: "array", editable: true, allowAdd: true, allowDelete: true,
        //       items: { type: "enum", editable: true, options: ["CA", "TX", "FL"] }
        //     }
        //   }
        const childSchema =
          overrides?.[childPath.join('.')] ??
          resolveFieldSchema(schema, keyName)
        const isObj = typeof value === 'object' && value !== null
        const isArr = Array.isArray(value)

        const childEditable = !!childSchema?.editable
        const deleteBtn =
          showDeleteBtn && childEditable ? (
            <button
              type='button'
              className={cxEditor('delete-button')}
              onClick={() => onDeleteKey(path, keyName)}
              aria-label={`Delete field ${keyName}`}
            >
              <Icon name='times' iconStyle='light' color='error' size='small' />
            </button>
          ) : null

        if (isObj || isArr) {
          // array of primitives
          if (isArr && !isArrayOfObjects(value as Node[])) {
            // Editable primitive arrays get their own EditorArrayNode
            // (which has its own edit toggle)
            if (
              effectiveCanEdit &&
              childSchema?.type === 'array' &&
              childSchema.editable
            ) {
              return (
                <div
                  className={cxViewer('json-node', classNames?.value)}
                  key={keyName}
                >
                  {keyName && (
                    <NestedKeyLabel keyName={keyName} classNames={classNames} />
                  )}
                  <EditorArrayNode
                    data={value as Node[]}
                    schema={childSchema}
                    level={lvl}
                    path={childPath}
                    classNames={classNames}
                    onFieldChange={onFieldChange}
                    onDeleteItem={onDeleteItem}
                    onAddItem={onAddItem}
                    onDeleteKey={onDeleteKey}
                    canEdit={effectiveCanEdit}
                    editingPaths={editingRef.current}
                    toggleEditing={toggleEditing}
                    overrides={overrides}
                  />
                  {deleteBtn}
                </div>
              )
            }
            return (
              <ReadOnlyPrimitiveArray
                key={keyName}
                keyName={keyName}
                value={value as Node[]}
                classNames={classNames}
              />
            )
          }
          // object or array of objects
          return (
            <div
              className={cxViewer('json-node', classNames?.value)}
              key={keyName}
            >
              {keyName && (
                <NestedKeyLabel keyName={keyName} classNames={classNames} />
              )}
              <EditorNode
                data={value}
                schema={childSchema}
                level={lvl + (isArr ? 0 : 1)}
                path={childPath}
                classNames={classNames}
                onFieldChange={onFieldChange}
                onDeleteItem={onDeleteItem}
                onAddItem={onAddItem}
                onDeleteKey={onDeleteKey}
                canEdit={effectiveCanEdit}
                editingPaths={editingRef.current}
                toggleEditing={toggleEditing}
                overrides={overrides}
              />
              {deleteBtn}
            </div>
          )
        }

        // Primitive value – check editability
        return renderEditableValue(
          value,
          keyName,
          childPath,
          childSchema,
          deleteBtn
        )
      },
      [
        schema,
        path,
        effectiveCanEdit,
        classNames,
        onFieldChange,
        onDeleteItem,
        onAddItem,
        onDeleteKey,
        overrides,
        renderEditableValue,
        toggleEditing,
      ]
    )

    // ── Object rendering ────────────────────────────────────────────────
    const objSchema = schema?.type === 'object' ? schema : undefined
    const objAllowDelete =
      effectiveCanEdit &&
      isEditing &&
      !!objSchema?.editable &&
      !!objSchema?.allowDelete

    const renderObjectNode = useCallback(
      (objData: Record<string, Node>, lvl: number) => {
        return _.isEmpty(objData) ? (
          <ReadOnlyEmptyObject classNames={classNames} />
        ) : (
          <>
            {Object.entries(objData).map(([key, value]) =>
              renderValue(value, key, lvl, objAllowDelete)
            )}
          </>
        )
      },
      [classNames, renderValue, objAllowDelete]
    )

    // ── Array of objects ────────────────────────────────────────────────
    if (isArray && isArrayOfObjects(data as Node[])) {
      return (
        <EditorArrayNode
          data={data as Node[]}
          schema={schema?.type === 'array' ? schema : undefined}
          level={level}
          path={path}
          classNames={classNames}
          onFieldChange={onFieldChange}
          onDeleteItem={onDeleteItem}
          onAddItem={onAddItem}
          onDeleteKey={onDeleteKey}
          canEdit={effectiveCanEdit}
          editingPaths={editingPaths}
          toggleEditing={toggleEditing}
          overrides={overrides}
        />
      )
    }

    // ── Container node ──────────────────────────────────────────────────
    return (
      <div
        className={cxViewer(
          'json-node-container',
          backgroundColorClass,
          { editing: isEditing },
          classNames?.container
        )}
        data-level={level}
      >
        {showActionsBar && (
          <div className={cxEditor('node-actions-bar')}>
            {name && (
              <Text
                size='tiny'
                color='white'
                className={cxEditor('node-actions-badge')}
              >
                {toProperCase(name)}
              </Text>
            )}
            {showEditToggle && (
              <button
                type='button'
                className={cxEditor('action-button', { active: isEditing })}
                onClick={() => toggleEditing(myPathKey)}
                aria-label={
                  isEditing ? 'Stop editing this block' : 'Edit this block'
                }
              >
                <Icon
                  name={isEditing ? 'check' : 'pencil'}
                  iconStyle='light'
                  size='small'
                />
              </button>
            )}
            {isEditing && onDelete && (
              <button
                type='button'
                className={cxEditor('action-button')}
                onClick={onDelete}
                aria-label='Delete this item'
              >
                <Icon
                  name='trash-alt'
                  iconStyle='light'
                  color='error'
                  size='small'
                />
              </button>
            )}
          </div>
        )}
        <>
          {/* eslint-disable-next-line no-nested-ternary */}
          {isObject ? (
            isArray ? (
              <EditorArrayNode
                data={data as Node[]}
                schema={schema?.type === 'array' ? schema : undefined}
                level={level}
                path={path}
                classNames={classNames}
                onFieldChange={onFieldChange}
                onDeleteItem={onDeleteItem}
                onAddItem={onAddItem}
                onDeleteKey={onDeleteKey}
                canEdit={effectiveCanEdit}
                editingPaths={editingPaths}
                toggleEditing={toggleEditing}
                overrides={overrides}
              />
            ) : (
              renderObjectNode(data as Record<string, Node>, level)
            )
          ) : (
            renderEditableValue(data, name || '', path, schema)
          )}
        </>
      </div>
    )
  }
)

EditorNode.displayName = 'EditorNode'

// ---------------------------------------------------------------------------
// Array node with add/delete controls
// ---------------------------------------------------------------------------

interface EditorArrayNodeProps {
  data: Node[]
  schema: JsonFieldSchema | undefined
  level: number
  path: (string | number)[]
  classNames?: JsonViewerClassNames
  onFieldChange: (path: (string | number)[], value: Node) => void
  onDeleteItem: (arrayPath: (string | number)[], index: number) => void
  onAddItem: (
    arrayPath: (string | number)[],
    itemSchema: JsonFieldSchema
  ) => void
  onDeleteKey: (objectPath: (string | number)[], key: string) => void
  canEdit: boolean
  editingPaths: Set<string>
  toggleEditing: (pathKey: string) => void
  /** Path-level schema overrides from `JsonEditorSchema.overrides`. */
  overrides?: Record<string, JsonFieldSchema>
}

const EditorArrayNode: React.FC<EditorArrayNodeProps> = React.memo(
  ({
    data,
    schema,
    level,
    path,
    classNames,
    onFieldChange,
    onDeleteItem,
    onAddItem,
    onDeleteKey,
    canEdit,
    editingPaths,
    toggleEditing,
    overrides,
  }) => {
    const arraySchema = schema?.type === 'array' ? schema : undefined
    const allowDelete =
      canEdit && !!arraySchema?.editable && !!arraySchema?.allowDelete
    const allowAdd =
      canEdit && !!arraySchema?.editable && !!arraySchema?.allowAdd

    const isObjectArray = isArrayOfObjects(data)

    // For primitive arrays, use the array's own path for edit toggle
    const myKey = pathKey(path)
    const isEditing = editingPaths.has(myKey)
    const showEditToggle =
      canEdit && !!arraySchema?.editable && !!arraySchema?.items?.editable

    if (!isObjectArray) {
      const itemSchema = arraySchema?.items
      return (
        <div className={cxViewer('json-array', { editing: isEditing })}>
          {showEditToggle && (
            <div
              className={cxEditor('node-actions-bar')}
              style={{ position: 'relative', marginBottom: 4 }}
            >
              <button
                type='button'
                className={cxEditor('action-button', { active: isEditing })}
                onClick={() => toggleEditing(myKey)}
                aria-label={isEditing ? 'Finish editing' : 'Edit array'}
              >
                <Icon
                  name={isEditing ? 'check' : 'pencil'}
                  iconStyle='light'
                  size='small'
                />
              </button>
            </div>
          )}
          {data.map((item: Node, index: number) => {
            const itemPath = [...path, index]
            const itemEditable =
              canEdit &&
              isEditing &&
              !!itemSchema?.editable &&
              itemSchema &&
              schemaMatchesValue(itemSchema, item)

            if (itemEditable && itemSchema) {
              return (
                <div key={index} className={cxEditor('array-item-row')}>
                  <div className={cxEditor('editable-value')}>
                    <InlineFieldByType
                      value={item}
                      schema={itemSchema}
                      onChange={v => onFieldChange(itemPath, v)}
                      elmId={itemPath.join('_')}
                    />
                  </div>
                  {allowDelete && (
                    <button
                      type='button'
                      className={cxEditor('delete-button')}
                      onClick={() => onDeleteItem(path, index)}
                      aria-label={`Delete item ${index}`}
                    >
                      <Icon
                        name='times'
                        iconStyle='light'
                        color='error'
                        size='small'
                      />
                    </button>
                  )}
                </div>
              )
            }

            return (
              <div key={index} className={cxEditor('array-item-row')}>
                <Text
                  size='tiny'
                  className={cxViewer('json-value-text', classNames?.valueText)}
                  weight='heavy'
                >
                  {JSON.stringify(item)}
                </Text>
                {allowDelete && isEditing && (
                  <button
                    type='button'
                    className={cxEditor('delete-button')}
                    onClick={() => onDeleteItem(path, index)}
                    aria-label={`Delete item ${index}`}
                  >
                    <Icon
                      name='times'
                      iconStyle='light'
                      color='error'
                      size='small'
                    />
                  </button>
                )}
              </div>
            )
          })}
          {allowAdd && isEditing && arraySchema?.items && (
            <div className={cxEditor('add-button-row')}>
              <Button
                role='tertiary'
                size='small'
                elmId={`${path.join('_')}_add`}
                onClick={() => onAddItem(path, arraySchema.items)}
              >
                <Icon name='plus' iconStyle='light' size='small' /> Add Item
              </Button>
            </div>
          )}
        </div>
      )
    }

    // Array of objects — each item gets its own container (with its own edit toggle)
    return (
      <div className={cxViewer('json-array')}>
        {data.map((item: Node, index: number) => (
          <EditorNode
            key={index}
            data={item}
            schema={arraySchema?.items}
            name={`${index}`}
            level={level + 1}
            path={[...path, index]}
            classNames={classNames}
            onFieldChange={onFieldChange}
            onDeleteItem={onDeleteItem}
            onAddItem={onAddItem}
            onDeleteKey={onDeleteKey}
            canEdit={canEdit}
            editingPaths={editingPaths}
            toggleEditing={toggleEditing}
            onDelete={allowDelete ? () => onDeleteItem(path, index) : undefined}
            overrides={overrides}
          />
        ))}
        {allowAdd && arraySchema?.items && (
          <div className={cxEditor('add-button-row')}>
            <Button
              role='tertiary'
              size='small'
              elmId={`${path.join('_')}_add`}
              onClick={() => onAddItem(path, arraySchema.items)}
            >
              <Icon name='plus' iconStyle='light' size='small' /> Add Item
            </Button>
          </div>
        )}
      </div>
    )
  }
)

EditorArrayNode.displayName = 'EditorArrayNode'

export default JsonEditor
